import type { MfeConfig, MfeVersionManagerInstanceType } from './types';
// Import single function, not the 'semver' index, to keep the client bundle small
import valid from 'semver/functions/valid';

const MFE_VERSION_COOKIE_PREFIX = 'mfe-version-';
const MFE_SERVICE_URL_COOKIE_PREFIX = 'mfe-service-url-';

/**
 * A utility class to manage versions and service URLs for Micro-Frontends (MFEs).
 *
 * It retrieves MFE configurations based on a hierarchy:
 * 1. A value specified in a cookie (for overrides during development/testing).
 * 2. A value specified in the customer configuration.
 * 3. A default value as a fallback.
 */
export class MfeVersionManager implements MfeVersionManagerInstanceType {
  private mfeConfig: MfeConfig;
  private cookies: Record<string, string>;
  private req?: { cookies: Record<string, string> };

  /**
   * Creates an instance of MfeVersionManager.
   * This class is isomorphic and can be used on both the server and the client.
   *
   * @param mfeConfig The MFE configuration object.
   * @param req Optional Express request object for server-side rendering.
   */
  constructor(mfeConfig: MfeConfig, req?: { cookies: Record<string, string> }) {
    this.mfeConfig = mfeConfig;
    this.req = req;
    this.cookies = this.getCookieValues();
  }

  // initialize global mfe manager
  static initialize(config: MfeConfig) {
    if (typeof window !== 'undefined') {
      (window as any).__MFE_VERSION_MANAGER__ = new MfeVersionManager(config);
      return (window as any).__MFE_VERSION_MANAGER__;
    }
    return null;
  }

  private getCookieValues() {
    let cookies = {};
    if (this.req) {
      // Server-side: Use cookies from the request object
      cookies = this.req.cookies || {};
    } else if (
      typeof window !== 'undefined' &&
      typeof window.document !== 'undefined'
    ) {
      // Client-side: Parse cookies from document.cookie
      cookies = this.parseCookies(window.document.cookie);
    }
    return cookies;
  }

  public refresh() {
    this.cookies = this.getCookieValues();
  }

  public getMfeNames() {
    return Object.keys(this.mfeConfig);
  }

  /**
   * Parses a cookie string into a key-value object.
   * @param cookieString The cookie string to parse.
   * @returns An object of cookie key-value pairs.
   */
  private parseCookies(cookieString: string): Record<string, string> {
    if (!cookieString) {
      return {};
    }
    return cookieString.split(';').reduce(
      (acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        if (key && value) {
          acc[key] = decodeURIComponent(value);
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }

  /**
   * Gets the version for a specific MFE.
   *
   * The version is determined in the following order:
   * 1. From a cookie named `mfe-version-<mfeName>`.
   * 2. From the customer config at `mfe_versions.<mfeName>`.
   * 3. Defaults to 'current'.
   *
   * @param mfeName The name of the MFE (e.g., 'scheduling-ui').
   * @returns The version string, unchanged from the resolved pin.
   */
  public getVersion(mfeName: string): string {
    // 1. Check for a cookie override
    const cookieName = `${MFE_VERSION_COOKIE_PREFIX}${mfeName}`;
    const cookieVersion = this.cookies[cookieName];
    if (cookieVersion) {
      return cookieVersion;
    }

    // 2. Check for a version in the customer config
    const configVersion = this.mfeConfig[mfeName]?.version;
    if (configVersion) {
      return configVersion;
    }

    // 3. Return the default version
    return 'current';
  }

  public setVersionOverride(mfeName: string, version: string | null) {
    const cookieName = `${MFE_VERSION_COOKIE_PREFIX}${mfeName}`;
    if (version) {
      window.document.cookie = `${cookieName}=${encodeURIComponent(
        version
      )};path=/`;
    } else {
      window.document.cookie = `${cookieName}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    }
  }

  public isVersionOverridden(mfeName: string): boolean {
    const cookieName = `${MFE_VERSION_COOKIE_PREFIX}${mfeName}`;
    return this.cookies[cookieName] !== undefined;
  }

  /**
   * Gets the service base URL for a specific MFE.
   *
   * The URL is determined in the following order:
   * 1. From a cookie named `mfe-service-url-<mfeName>`.
   * 2. From the customer config at `mfe_service_urls.<mfeName>`.
   * 3. From the customer config at `service_url` (as a fallback).
   * 4. Defaults to an empty string.
   *
   * @param mfeName The name of the MFE (e.g., 'scheduling-ui').
   * @returns The service base URL string.
   */
  public getServiceBaseUrl(mfeName: string): string {
    // 1. Check for a cookie override
    const cookieName = `${MFE_SERVICE_URL_COOKIE_PREFIX}${mfeName}`;
    const cookieUrl = this.cookies[cookieName];
    if (cookieUrl) {
      return cookieUrl;
    }

    // 2. Check for an MFE-specific URL in the customer config
    const configMfeUrl = this.mfeConfig[mfeName]?.urlTemplate;
    if (configMfeUrl) {
      return configMfeUrl;
    }

    // 4. Return the default
    return '';
  }

  public isServiceBaseUrlOverridden(mfeName: string): boolean {
    const cookieName = `${MFE_SERVICE_URL_COOKIE_PREFIX}${mfeName}`;
    return this.cookies[cookieName] !== undefined;
  }

  public setServiceBaseUrlOverride(mfeName: string, url: string | null) {
    const cookieName = `${MFE_SERVICE_URL_COOKIE_PREFIX}${mfeName}`;
    if (url) {
      window.document.cookie = `${cookieName}=${encodeURIComponent(url)};path=/`;
    } else {
      window.document.cookie = `${cookieName}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    }
  }
  public getResolvedUrl(mfeName: string) {
    const templateUrl = this.getServiceBaseUrl(mfeName);
    if (templateUrl) {
      const version = this.getVersion(mfeName);
      // Normalize valid semver (strips leading 'v'), otherwise pass through
      const normalizedVersion = valid(version) ?? version;
      return templateUrl.replace('{version}', normalizedVersion);
    }
    return templateUrl;
  }
}
